import React, { useState } from "react";
import {
  CreditCard,
  FileText,
  Sparkles,
  Shield,
  Eye,
  Maximize2,
  X,
  CheckCircle2,
  QrCode,
  Barcode,
  Layers,
  ArrowRight,
  Info,
  ExternalLink,
} from "lucide-react";

export interface NINSlipSamplePreviewProps {
  selectedSlug?: string | null;
  onSelectVariant?: (slug: string) => void;
  isSandbox?: boolean;
}

interface VariantMeta {
  slug: string;
  name: string;
  shortBadge: string;
  badgeClass: string;
  cost: string;
  dimensions: string;
  resolution: string;
  features: string[];
  recommendedFor: string;
  description: string;
}

export const NIN_VARIANTS_META: Record<string, VariantMeta> = {
  "nin-digital-card": {
    slug: "nin-digital-card",
    name: "NIN Digital ID Card",
    shortBadge: "4K Wallet Card",
    badgeClass: "cht-bg-indigo-50 cht-text-indigo-700 cht-border-indigo-200",
    cost: "500.00",
    dimensions: "CR80 (85.60 × 53.98 mm) - Standard PVC Card",
    resolution: "4K Ultra-Crisp (300+ DPI Print-Ready)",
    features: [
      "Dual-sided wallet card layout (Front & Back)",
      "High-contrast QR code with instant camera scan",
      "Official Coat of Arms & holographic guilloche",
      "Formatted 4-3-4 bold National ID Number",
      "Machine-Readable Zone (MRZ) on back face",
    ],
    recommendedFor: "Direct printing onto PVC plastic ID cards, physical wallet carrying, and smart-card compliance.",
    description: "Dual-sided wallet-sized digital identity card with front citizen profile and back security disclaimer.",
  },
  "nin-premium-slip": {
    slug: "nin-premium-slip",
    name: "NIN Premium Slip",
    shortBadge: "Premium Gloss",
    badgeClass: "cht-bg-amber-50 cht-text-amber-800 cht-border-amber-200",
    cost: "200.00",
    dimensions: "Gloss Certificate Card (with wallet cutout guides)",
    resolution: "High Resolution Vector Typography",
    features: [
      "Security guilloche background watermark",
      "Cryptographic smartphone QR Code verification",
      "Dual Federal Republic of Nigeria & NIMC logos",
      "Clean cut-out border guidelines for laminating",
      "Centered 4-3-4 formatted NIN digits",
    ],
    recommendedFor: "Banking KYC, formal corporate compliance, and laminated wallet certificates.",
    description: "High-resolution premium gloss certificate layout with security guilloche pattern and QR verification.",
  },
  "nin-standard-slip": {
    slug: "nin-standard-slip",
    name: "NIN Standard Slip",
    shortBadge: "Standard Slip",
    badgeClass: "cht-bg-blue-50 cht-text-[#3e57da] cht-border-blue-200",
    cost: "200.00",
    dimensions: "Standard A4 Official Government Document",
    resolution: "Official Agency Print Quality",
    features: [
      "Official Code128 Linear Tracking Barcode",
      "Federal Coat of Arms and official NIMC logo",
      "Full citizen demographic bio-data table",
      "Official government tracking ID reference",
      "Authentic proof of NIN registration layout",
    ],
    recommendedFor: "Government ministry submissions, visa applications, and administrative record filings.",
    description: "Standard government NIN slip download with barcode verification and citizen bio-data.",
  },
  "nin-personal-slip": {
    slug: "nin-personal-slip",
    name: "Personal Information Slip",
    shortBadge: "Bio Slip",
    badgeClass: "cht-bg-emerald-50 cht-text-emerald-700 cht-border-emerald-200",
    cost: "200.00",
    dimensions: "A4 Digital Demographic Sheet",
    resolution: "Clear Biometric & Demographic Layout",
    features: [
      "Demographic information breakdown",
      "High-contrast citizen photograph",
      "Verified address, LGA, and State of Origin",
      "Official confidential identity disclaimer",
    ],
    recommendedFor: "Student registrations, personnel onboarding, and personal bio-data archiving.",
    description: "Personal identification slip with demographic details, photograph, and digital signature.",
  },
};

const SAMPLE_CITIZEN = {
  surname: "MUSA",
  firstName: "CHUKWUEMEKA",
  middleName: "IBRAHIM",
  dob: "15  MAY  1994",
  gender: "M",
  ninFormatted: "5492    108    4329",
  ninRaw: "54921084329",
  trackingId: "NG-84920-19420-14",
  issueDate: "28  OCT  2026",
  address: "Plot 14, Commercial Avenue, Ikeja",
  state: "Lagos State",
  lga: "Ikeja",
  photoUrl: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&auto=format&fit=crop&q=80",
};

export const NINSlipSamplePreview: React.FC<NINSlipSamplePreviewProps> = ({
  selectedSlug,
  onSelectVariant,
  isSandbox = false,
}) => {
  const activeSlug = selectedSlug || "nin-digital-card";
  const [cardFace, setCardFace] = useState<"front" | "back">("front");
  const [isLightboxOpen, setIsLightboxOpen] = useState<boolean>(false);

  const activeMeta = NIN_VARIANTS_META[activeSlug] || NIN_VARIANTS_META["nin-digital-card"];

  return (
    <div className="cht-space-y-4 cht-rounded-2xl cht-bg-white cht-p-5 cht-border cht-border-slate-200/80 cht-shadow-soft">
      {/* Header bar */}
      <div className="cht-flex cht-items-center cht-justify-between cht-border-b cht-border-gray-100 cht-pb-3.5">
        <div className="cht-flex cht-items-center cht-gap-2.5">
          <div className="cht-w-8 cht-h-8 cht-rounded-xl cht-bg-emerald-50 cht-text-emerald-700 cht-flex cht-items-center cht-justify-center">
            <Eye size={16} />
          </div>
          <div>
            <div className="cht-flex cht-items-center cht-gap-2">
              <h4 className="cht-text-xs cht-font-extrabold cht-text-gray-900">
                Interactive Document Sample Preview
              </h4>
              <span className="cht-px-2 cht-py-0.5 cht-rounded-full cht-text-[10px] cht-font-bold cht-bg-indigo-50 cht-text-indigo-600 cht-border cht-border-indigo-100">
                Visual Proof
              </span>
            </div>
            <p className="cht-text-[11px] cht-text-gray-400">
              Realistic preview of the generated document layout before execution
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setIsLightboxOpen(true)}
          className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-2.5 cht-py-1.5 cht-rounded-lg cht-border cht-border-gray-200 cht-text-[11px] cht-font-bold cht-text-gray-600 hover:cht-bg-gray-50 hover:cht-text-[#3e57da] cht-transition-colors"
          title="Inspect Fullscreen Sample"
        >
          <Maximize2 size={12} />
          <span className="cht-hidden sm:cht-inline">Enlarge Preview</span>
        </button>
      </div>

      {/* Sub-Service Variant Selector Tabs */}
      <div className="cht-grid cht-grid-cols-2 sm:cht-grid-cols-4 cht-gap-1.5">
        {Object.values(NIN_VARIANTS_META).map((v) => {
          const isSelected = activeSlug === v.slug;
          return (
            <button
              key={v.slug}
              type="button"
              onClick={() => {
                onSelectVariant?.(v.slug);
              }}
              className={`cht-py-2 cht-px-2.5 cht-rounded-xl cht-text-left cht-transition-all cht-border ${
                isSelected
                  ? "cht-bg-[#3e57da] cht-text-white cht-border-[#3e57da] cht-shadow-sm"
                  : "cht-bg-gray-50/70 cht-text-gray-600 cht-border-gray-200 hover:cht-bg-white hover:cht-border-gray-300"
              }`}
            >
              <div className="cht-text-[11px] cht-font-black cht-truncate">{v.name}</div>
              <div className={`cht-text-[9px] cht-font-semibold cht-truncate ${isSelected ? "cht-text-indigo-100" : "cht-text-gray-400"}`}>
                {v.shortBadge} • {isSandbox ? "₦0.00" : `₦${parseFloat(v.cost).toFixed(0)}`}
              </div>
            </button>
          );
        })}
      </div>

      {/* Visual Mockup Container */}
      <div className="cht-bg-slate-900 cht-rounded-2xl cht-p-4 sm:cht-p-6 cht-flex cht-flex-col cht-items-center cht-justify-center cht-relative cht-overflow-hidden cht-border cht-border-slate-800">
        <div className="cht-absolute cht-inset-0 cht-bg-gradient-to-tr cht-from-emerald-950/20 cht-via-indigo-950/30 cht-to-slate-900 cht-pointer-events-none" />

        {/* Digital Card Toggle (if Digital Card) */}
        {activeSlug === "nin-digital-card" && (
          <div className="cht-flex cht-items-center cht-gap-1 cht-bg-slate-800/90 cht-p-1 cht-rounded-xl cht-mb-4 cht-border cht-border-slate-700 cht-z-10">
            <button
              type="button"
              onClick={() => setCardFace("front")}
              className={`cht-px-3 cht-py-1 cht-rounded-lg cht-text-[10px] cht-font-bold cht-transition-all ${
                cardFace === "front"
                  ? "cht-bg-[#3e57da] cht-text-white cht-shadow-sm"
                  : "cht-text-slate-400 hover:cht-text-white"
              }`}
            >
              Card Front Face
            </button>
            <button
              type="button"
              onClick={() => setCardFace("back")}
              className={`cht-px-3 cht-py-1 cht-rounded-lg cht-text-[10px] cht-font-bold cht-transition-all ${
                cardFace === "back"
                  ? "cht-bg-[#3e57da] cht-text-white cht-shadow-sm"
                  : "cht-text-slate-400 hover:cht-text-white"
              }`}
            >
              Card Back Face (Security Details)
            </button>
          </div>
        )}

        {/* SAMPLE RENDERER */}
        <div className="cht-w-full cht-flex cht-justify-center cht-z-10">
          {activeSlug === "nin-digital-card" && (
            <div className="cht-w-full cht-max-w-md">
              {cardFace === "front" ? (
                /* DIGITAL CARD FRONT FACE */
                <div
                  className="cht-w-full cht-aspect-[1.586/1] cht-rounded-2xl cht-p-3.5 sm:cht-p-4 cht-relative cht-overflow-hidden cht-shadow-2xl cht-border cht-border-emerald-500/40 cht-flex cht-flex-col cht-justify-between cht-bg-gradient-to-br cht-from-[#043927] cht-via-[#065f46] cht-to-[#042f2e] cht-text-white"
                  style={{
                    backgroundImage: `linear-gradient(rgba(4, 57, 39, 0.88), rgba(4, 47, 46, 0.92)), url('/assets/nin/digital_nin_front_template.jpg')`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                >
                  {/* Top Header */}
                  <div className="cht-flex cht-items-start cht-justify-between cht-gap-2">
                    <div className="cht-flex cht-items-center cht-gap-2">
                      <img
                        src="/assets/nin/Nigerias-Coat-Of-Arms.jpg"
                        alt="Coat of Arms"
                        className="cht-w-7 cht-h-7 cht-object-contain cht-rounded-sm"
                        onError={(e) => {
                          (e.target as HTMLElement).style.display = "none";
                        }}
                      />
                      <div>
                        <div className="cht-text-[9px] sm:cht-text-[10px] cht-font-black cht-tracking-widest cht-text-emerald-200">
                          FEDERAL REPUBLIC OF NIGERIA
                        </div>
                        <div className="cht-text-[8px] sm:cht-text-[9px] cht-font-bold cht-text-slate-200">
                          National Identity Management Commission
                        </div>
                      </div>
                    </div>
                    <span className="cht-px-2 cht-py-0.5 cht-rounded-full cht-bg-emerald-500/20 cht-border cht-border-emerald-400/30 cht-text-[8px] cht-font-black cht-text-emerald-300">
                      DIGITAL NIN SLIP
                    </span>
                  </div>

                  {/* Body Content */}
                  <div className="cht-flex cht-items-center cht-gap-3 cht-my-auto">
                    {/* Portrait Photo */}
                    <div className="cht-w-16 sm:cht-w-20 cht-h-20 sm:cht-h-24 cht-rounded-xl cht-overflow-hidden cht-border-2 cht-border-emerald-400/60 cht-shadow-md cht-shrink-0 cht-bg-slate-800">
                      <img
                        src={SAMPLE_CITIZEN.photoUrl}
                        alt="Citizen Passport Sample"
                        className="cht-w-full cht-h-full cht-object-cover"
                      />
                    </div>

                    {/* Citizen Details */}
                    <div className="cht-flex-1 cht-min-w-0 cht-space-y-1">
                      <div>
                        <div className="cht-text-[7px] cht-text-emerald-300/80 cht-font-bold cht-uppercase">
                          Surname
                        </div>
                        <div className="cht-text-xs sm:cht-text-sm cht-font-black cht-tracking-wide cht-text-white cht-truncate">
                          {SAMPLE_CITIZEN.surname}
                        </div>
                      </div>
                      <div>
                        <div className="cht-text-[7px] cht-text-emerald-300/80 cht-font-bold cht-uppercase">
                          Given Names
                        </div>
                        <div className="cht-text-[10px] sm:cht-text-xs cht-font-bold cht-text-slate-100 cht-truncate">
                          {SAMPLE_CITIZEN.firstName} {SAMPLE_CITIZEN.middleName}
                        </div>
                      </div>
                      <div className="cht-flex cht-items-center cht-gap-3 cht-text-[8px] cht-font-semibold cht-text-slate-200">
                        <div>
                          <span className="cht-text-emerald-300/80">DOB: </span>
                          <span>{SAMPLE_CITIZEN.dob}</span>
                        </div>
                        <div>
                          <span className="cht-text-emerald-300/80">SEX: </span>
                          <span>{SAMPLE_CITIZEN.gender}</span>
                        </div>
                      </div>
                    </div>

                    {/* QR Code */}
                    <div className="cht-w-14 sm:cht-w-16 cht-h-14 sm:cht-h-16 cht-p-1 cht-bg-white cht-rounded-xl cht-shadow-sm cht-shrink-0 cht-flex cht-flex-col cht-items-center cht-justify-center">
                      <QrCode size={48} className="cht-text-slate-900" />
                      <span className="cht-text-[6px] cht-font-black cht-text-emerald-800">VERIFIED</span>
                    </div>
                  </div>

                  {/* Bottom Bold NIN Bar */}
                  <div className="cht-pt-1.5 cht-border-t cht-border-emerald-500/30 cht-text-center">
                    <div className="cht-text-[7px] sm:cht-text-[8px] cht-font-bold cht-tracking-wider cht-text-emerald-200 cht-uppercase">
                      National Identification Number (NIN)
                    </div>
                    <div className="cht-text-sm sm:cht-text-base cht-font-black cht-font-mono cht-tracking-widest cht-text-white">
                      {SAMPLE_CITIZEN.ninFormatted}
                    </div>
                  </div>
                </div>
              ) : (
                /* DIGITAL CARD BACK FACE */
                <div
                  className="cht-w-full cht-aspect-[1.586/1] cht-rounded-2xl cht-p-4 sm:cht-p-5 cht-relative cht-overflow-hidden cht-shadow-2xl cht-border cht-border-emerald-500/40 cht-flex cht-flex-col cht-justify-between cht-bg-gradient-to-br cht-from-[#032e1f] cht-via-[#044331] cht-to-[#022c22] cht-text-white"
                  style={{
                    backgroundImage: `linear-gradient(rgba(3, 46, 31, 0.90), rgba(2, 44, 34, 0.94)), url('/assets/nin/digital_nin_back_template.jpg')`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                >
                  <div className="cht-space-y-1.5">
                    <div className="cht-text-[9px] cht-font-black cht-text-emerald-300 cht-uppercase cht-tracking-widest">
                      Terms & Legal Disclaimer
                    </div>
                    <p className="cht-text-[8px] cht-leading-relaxed cht-text-slate-200">
                      The details on the front of this NIN Slip must EXACTLY match the verification result.
                      This card remains the property of the Federal Republic of Nigeria. If found, please return
                      to any NIMC state or local government office, or the nearest police station.
                    </p>
                  </div>

                  {/* Holder Signature & Contact Details */}
                  <div className="cht-flex cht-items-end cht-justify-between cht-gap-3 cht-py-2 cht-border-y cht-border-emerald-500/30">
                    <div>
                      <div className="cht-text-[7px] cht-text-emerald-300 cht-font-bold cht-uppercase">
                        Emergency & Support Hotline
                      </div>
                      <div className="cht-text-[9px] cht-font-mono cht-font-bold cht-text-white">
                        0700-CALL-NIMC (0700-225-5646)
                      </div>
                      <div className="cht-text-[8px] cht-text-emerald-200 cht-font-mono">
                        www.nimc.gov.ng
                      </div>
                    </div>
                    <div className="cht-text-right">
                      <div className="cht-w-28 cht-border-b cht-border-dashed cht-border-slate-400 cht-mb-1" />
                      <div className="cht-text-[7px] cht-text-slate-300 cht-font-medium">
                        Authorized Holder Signature
                      </div>
                    </div>
                  </div>

                  {/* Machine Readable Zone (MRZ) */}
                  <div className="cht-font-mono cht-text-[7px] sm:cht-text-[8px] cht-tracking-widest cht-text-emerald-200/90 cht-leading-tight">
                    <div>I&lt;NGA54921084329&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;</div>
                    <div>9405152M2610284NGA&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;8</div>
                    <div>MUSA&lt;&lt;CHUKWUEMEKA&lt;IBRAHIM&lt;&lt;&lt;&lt;</div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeSlug === "nin-premium-slip" && (
            /* PREMIUM GLOSS CERTIFICATE SLIP */
            <div
              className="cht-w-full cht-max-w-md cht-aspect-[1.414/1] cht-bg-white cht-rounded-2xl cht-p-4 sm:cht-p-5 cht-relative cht-shadow-2xl cht-border-2 cht-border-dashed cht-border-emerald-600/60 cht-text-slate-800 cht-flex cht-flex-col cht-justify-between"
              style={{
                backgroundImage: `radial-gradient(circle at 50% 50%, rgba(16, 185, 129, 0.05) 0%, rgba(255, 255, 255, 0.98) 100%)`,
              }}
            >
              {/* Slip Header */}
              <div className="cht-flex cht-items-center cht-justify-between cht-border-b cht-border-emerald-200 cht-pb-2">
                <div className="cht-flex cht-items-center cht-gap-2">
                  <img
                    src="/assets/nin/Nigerias-Coat-Of-Arms.jpg"
                    alt="Coat of Arms"
                    className="cht-w-7 cht-h-7 cht-object-contain"
                    onError={(e) => {
                      (e.target as HTMLElement).style.display = "none";
                    }}
                  />
                  <div>
                    <div className="cht-text-[9px] cht-font-black cht-text-emerald-900 cht-tracking-wider">
                      FEDERAL REPUBLIC OF NIGERIA
                    </div>
                    <div className="cht-text-[8px] cht-font-bold cht-text-emerald-700">
                      National Identity Management Commission (NIMC)
                    </div>
                  </div>
                </div>
                <div className="cht-text-right">
                  <span className="cht-px-2 cht-py-0.5 cht-rounded-full cht-bg-amber-100 cht-text-amber-900 cht-text-[8px] cht-font-black cht-border cht-border-amber-300">
                    PREMIUM GLOSS EDITION
                  </span>
                </div>
              </div>

              {/* Citizen Details */}
              <div className="cht-flex cht-items-center cht-gap-3 cht-my-auto">
                <div className="cht-w-16 sm:cht-w-20 cht-h-20 sm:cht-h-24 cht-rounded-xl cht-overflow-hidden cht-border-2 cht-border-emerald-600 cht-shadow-sm cht-shrink-0 cht-bg-slate-100">
                  <img
                    src={SAMPLE_CITIZEN.photoUrl}
                    alt="Citizen"
                    className="cht-w-full cht-h-full cht-object-cover"
                  />
                </div>

                <div className="cht-flex-1 cht-min-w-0 cht-space-y-1 cht-text-[9px]">
                  <div>
                    <span className="cht-text-gray-400 cht-font-medium">Tracking ID: </span>
                    <span className="cht-font-mono cht-font-bold cht-text-gray-800">{SAMPLE_CITIZEN.trackingId}</span>
                  </div>
                  <div>
                    <span className="cht-text-gray-400 cht-font-medium">Full Name: </span>
                    <span className="cht-font-black cht-text-gray-900">
                      {SAMPLE_CITIZEN.surname}, {SAMPLE_CITIZEN.firstName} {SAMPLE_CITIZEN.middleName}
                    </span>
                  </div>
                  <div className="cht-flex cht-gap-3">
                    <div>
                      <span className="cht-text-gray-400">DOB: </span>
                      <span className="cht-font-bold">{SAMPLE_CITIZEN.dob}</span>
                    </div>
                    <div>
                      <span className="cht-text-gray-400">Gender: </span>
                      <span className="cht-font-bold">{SAMPLE_CITIZEN.gender}</span>
                    </div>
                  </div>
                  <div>
                    <span className="cht-text-gray-400">State/LGA: </span>
                    <span className="cht-font-bold">{SAMPLE_CITIZEN.state} / {SAMPLE_CITIZEN.lga}</span>
                  </div>
                </div>

                {/* QR Code */}
                <div className="cht-w-16 cht-h-16 cht-p-1 cht-bg-white cht-rounded-xl cht-border cht-border-emerald-300 cht-shrink-0 cht-flex cht-flex-col cht-items-center cht-justify-center">
                  <QrCode size={46} className="cht-text-slate-900" />
                  <span className="cht-text-[6px] cht-font-bold cht-text-emerald-700">SCAN QR</span>
                </div>
              </div>

              {/* Bold NIN Banner */}
              <div className="cht-p-2 cht-rounded-xl cht-bg-emerald-800 cht-text-white cht-text-center">
                <div className="cht-text-[8px] cht-font-bold cht-uppercase cht-tracking-wider cht-text-emerald-200">
                  National Identification Number (NIN)
                </div>
                <div className="cht-text-sm sm:cht-text-base cht-font-black cht-font-mono cht-tracking-widest">
                  {SAMPLE_CITIZEN.ninFormatted}
                </div>
              </div>
            </div>
          )}

          {activeSlug === "nin-standard-slip" && (
            /* STANDARD GOVERNMENT A4 SLIP */
            <div className="cht-w-full cht-max-w-md cht-aspect-[1.3/1] cht-bg-white cht-rounded-xl cht-p-4 cht-shadow-2xl cht-border cht-border-gray-300 cht-text-slate-800 cht-flex cht-flex-col cht-justify-between">
              {/* Top Header */}
              <div className="cht-text-center cht-border-b cht-border-gray-200 cht-pb-2">
                <div className="cht-text-[10px] cht-font-black cht-text-slate-900 cht-tracking-wide">
                  FEDERAL REPUBLIC OF NIGERIA
                </div>
                <div className="cht-text-[8px] cht-font-semibold cht-text-slate-600">
                  National Identity Management System
                </div>
                <div className="cht-text-[9px] cht-font-bold cht-text-[#3e57da] cht-mt-0.5">
                  National Identification Number Slip (NINS)
                </div>
              </div>

              {/* Details table */}
              <div className="cht-flex cht-items-center cht-gap-3 cht-my-auto">
                <div className="cht-w-16 cht-h-20 cht-rounded-lg cht-overflow-hidden cht-border cht-border-gray-300 cht-shrink-0 cht-bg-gray-100">
                  <img
                    src={SAMPLE_CITIZEN.photoUrl}
                    alt="Citizen"
                    className="cht-w-full cht-h-full cht-object-cover"
                  />
                </div>
                <div className="cht-flex-1 cht-text-[8px] cht-space-y-1">
                  <div className="cht-grid cht-grid-cols-2 cht-gap-1">
                    <div>
                      <span className="cht-text-gray-500">Tracking ID:</span>
                      <div className="cht-font-mono cht-font-bold">{SAMPLE_CITIZEN.trackingId}</div>
                    </div>
                    <div>
                      <span className="cht-text-gray-500">NIN:</span>
                      <div className="cht-font-mono cht-font-black cht-text-[#3e57da]">
                        {SAMPLE_CITIZEN.ninRaw}
                      </div>
                    </div>
                  </div>
                  <div>
                    <span className="cht-text-gray-500">Name:</span>
                    <div className="cht-font-bold cht-text-gray-900">
                      {SAMPLE_CITIZEN.surname}, {SAMPLE_CITIZEN.firstName} {SAMPLE_CITIZEN.middleName}
                    </div>
                  </div>
                  <div className="cht-grid cht-grid-cols-2 cht-gap-1">
                    <div>
                      <span className="cht-text-gray-500">DOB:</span>
                      <div className="cht-font-bold">{SAMPLE_CITIZEN.dob}</div>
                    </div>
                    <div>
                      <span className="cht-text-gray-500">Gender:</span>
                      <div className="cht-font-bold">{SAMPLE_CITIZEN.gender}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Barcode Footer */}
              <div className="cht-border-t cht-border-gray-200 cht-pt-2 cht-flex cht-items-center cht-justify-between">
                <div className="cht-flex cht-items-center cht-gap-1.5">
                  <Barcode size={32} className="cht-text-slate-800" />
                  <span className="cht-font-mono cht-text-[8px] cht-font-bold">{SAMPLE_CITIZEN.trackingId}</span>
                </div>
                <span className="cht-text-[7px] cht-text-gray-400">Official Government Record</span>
              </div>
            </div>
          )}

          {activeSlug === "nin-personal-slip" && (
            /* PERSONAL BIO-DATA SLIP */
            <div className="cht-w-full cht-max-w-md cht-aspect-[1.3/1] cht-bg-white cht-rounded-xl cht-p-4 cht-shadow-2xl cht-border cht-border-emerald-200 cht-text-slate-800 cht-flex cht-flex-col cht-justify-between">
              <div className="cht-border-b cht-border-emerald-100 cht-pb-2 cht-flex cht-items-center cht-justify-between">
                <div>
                  <div className="cht-text-[10px] cht-font-black cht-text-emerald-900">
                    National Identification Number (NIN)
                  </div>
                  <div className="cht-text-[8px] cht-font-bold cht-text-emerald-600">
                    Digital Personal Identification Certificate
                  </div>
                </div>
                <span className="cht-px-2 cht-py-0.5 cht-rounded-full cht-bg-emerald-50 cht-text-emerald-700 cht-text-[8px] cht-font-bold cht-border cht-border-emerald-200">
                  BIO SLIP
                </span>
              </div>

              <div className="cht-flex cht-items-center cht-gap-3 cht-my-auto">
                <div className="cht-w-16 cht-h-20 cht-rounded-lg cht-overflow-hidden cht-border-2 cht-border-emerald-500 cht-shrink-0">
                  <img
                    src={SAMPLE_CITIZEN.photoUrl}
                    alt="Citizen"
                    className="cht-w-full cht-h-full cht-object-cover"
                  />
                </div>
                <div className="cht-flex-1 cht-text-[8px] cht-space-y-1">
                  <div>
                    <span className="cht-text-gray-400">NIN: </span>
                    <span className="cht-font-mono cht-font-black cht-text-emerald-700">{SAMPLE_CITIZEN.ninRaw}</span>
                  </div>
                  <div>
                    <span className="cht-text-gray-400">Full Name: </span>
                    <span className="cht-font-bold cht-text-gray-900">
                      {SAMPLE_CITIZEN.surname} {SAMPLE_CITIZEN.firstName} {SAMPLE_CITIZEN.middleName}
                    </span>
                  </div>
                  <div>
                    <span className="cht-text-gray-400">Residential Address: </span>
                    <span className="cht-font-medium cht-text-gray-800">{SAMPLE_CITIZEN.address}</span>
                  </div>
                  <div>
                    <span className="cht-text-gray-400">Origin: </span>
                    <span className="cht-font-bold">{SAMPLE_CITIZEN.state} ({SAMPLE_CITIZEN.lga})</span>
                  </div>
                </div>
              </div>

              <div className="cht-p-1.5 cht-bg-emerald-50 cht-rounded-lg cht-border cht-border-emerald-200 cht-text-center cht-text-[7px] cht-text-emerald-800">
                NOTE: The National Identification Number (NIN) is confidential and unique to the citizen.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Specifications & Feature Badges */}
      <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 cht-gap-3 cht-pt-1">
        <div className="cht-space-y-1.5 cht-text-xs">
          <div className="cht-flex cht-items-center cht-gap-1.5 cht-font-bold cht-text-gray-800">
            <Layers size={13} className="cht-text-[#3e57da]" />
            <span>Specifications & Dimensions</span>
          </div>
          <div className="cht-text-[11px] cht-text-gray-600 cht-bg-gray-50 cht-p-2.5 cht-rounded-xl cht-border cht-border-gray-200/80 cht-space-y-1">
            <div>
              <span className="cht-text-gray-400">Size: </span>
              <span className="cht-font-semibold">{activeMeta.dimensions}</span>
            </div>
            <div>
              <span className="cht-text-gray-400">Resolution: </span>
              <span className="cht-font-semibold">{activeMeta.resolution}</span>
            </div>
            <div>
              <span className="cht-text-gray-400">Best Application: </span>
              <span className="cht-font-semibold">{activeMeta.recommendedFor}</span>
            </div>
          </div>
        </div>

        <div className="cht-space-y-1.5 cht-text-xs">
          <div className="cht-flex cht-items-center cht-gap-1.5 cht-font-bold cht-text-gray-800">
            <CheckCircle2 size={13} className="cht-text-emerald-600" />
            <span>Key Document Security Features</span>
          </div>
          <ul className="cht-text-[11px] cht-text-gray-600 cht-bg-gray-50 cht-p-2.5 cht-rounded-xl cht-border cht-border-gray-200/80 cht-space-y-1">
            {activeMeta.features.map((f, i) => (
              <li key={i} className="cht-flex cht-items-start cht-gap-1.5">
                <span className="cht-text-emerald-600 cht-font-black">•</span>
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* LIGHTBOX MODAL */}
      {isLightboxOpen && (
        <div className="cht-fixed cht-inset-0 cht-z-[9999] cht-flex cht-items-center cht-justify-center cht-bg-black/80 cht-backdrop-blur-md cht-p-4 cht-animate-in cht-fade-in cht-duration-200">
          <div className="cht-bg-white cht-rounded-squircle cht-shadow-2xl cht-border cht-border-slate-200 cht-w-full cht-max-w-2xl cht-p-6 cht-relative cht-space-y-4">
            <div className="cht-flex cht-items-center cht-justify-between cht-border-b cht-border-gray-100 cht-pb-3">
              <div>
                <h3 className="cht-text-sm cht-font-black cht-text-gray-900">{activeMeta.name} — Full High-Res Specimen</h3>
                <p className="cht-text-xs cht-text-gray-400">{activeMeta.dimensions}</p>
              </div>
              <button
                type="button"
                onClick={() => setIsLightboxOpen(false)}
                className="cht-p-1.5 cht-rounded-lg cht-text-gray-400 hover:cht-bg-gray-100 hover:cht-text-gray-700"
              >
                <X size={18} />
              </button>
            </div>

            <div className="cht-bg-slate-900 cht-rounded-2xl cht-p-6 sm:cht-p-8 cht-flex cht-flex-col cht-items-center cht-justify-center">
              {activeSlug === "nin-digital-card" && (
                <div className="cht-w-full cht-max-w-md">
                  <div className="cht-flex cht-justify-center cht-gap-2 cht-mb-4">
                    <button
                      type="button"
                      onClick={() => setCardFace("front")}
                      className={`cht-px-3 cht-py-1 cht-rounded-lg cht-text-xs cht-font-bold ${
                        cardFace === "front" ? "cht-bg-[#3e57da] cht-text-white" : "cht-bg-slate-800 cht-text-slate-300"
                      }`}
                    >
                      Front Face
                    </button>
                    <button
                      type="button"
                      onClick={() => setCardFace("back")}
                      className={`cht-px-3 cht-py-1 cht-rounded-lg cht-text-xs cht-font-bold ${
                        cardFace === "back" ? "cht-bg-[#3e57da] cht-text-white" : "cht-bg-slate-800 cht-text-slate-300"
                      }`}
                    >
                      Back Face
                    </button>
                  </div>

                  <img
                    src={cardFace === "front" ? "/assets/nin/digital_nin_front_template.jpg" : "/assets/nin/digital_nin_back_template.jpg"}
                    alt="Digital NIN Card Template Specimen"
                    className="cht-w-full cht-rounded-2xl cht-shadow-2xl cht-border cht-border-emerald-500/40"
                  />
                </div>
              )}

              {activeSlug !== "nin-digital-card" && (
                <div className="cht-w-full cht-max-w-md cht-bg-white cht-p-6 cht-rounded-2xl cht-shadow-2xl cht-text-slate-900">
                  <div className="cht-text-center cht-border-b cht-border-gray-200 cht-pb-3 cht-mb-4">
                    <div className="cht-text-xs cht-font-black">FEDERAL REPUBLIC OF NIGERIA</div>
                    <div className="cht-text-[10px] cht-text-emerald-700 cht-font-bold">{activeMeta.name}</div>
                  </div>
                  <div className="cht-flex cht-items-center cht-gap-4 cht-mb-4">
                    <img src={SAMPLE_CITIZEN.photoUrl} alt="Sample" className="cht-w-20 cht-h-24 cht-rounded-xl cht-object-cover cht-border-2 cht-border-emerald-600" />
                    <div className="cht-text-xs cht-space-y-1">
                      <div><span className="cht-text-gray-400">Surname: </span><span className="cht-font-bold">{SAMPLE_CITIZEN.surname}</span></div>
                      <div><span className="cht-text-gray-400">Given Names: </span><span className="cht-font-bold">{SAMPLE_CITIZEN.firstName} {SAMPLE_CITIZEN.middleName}</span></div>
                      <div><span className="cht-text-gray-400">Date of Birth: </span><span>{SAMPLE_CITIZEN.dob}</span></div>
                      <div><span className="cht-text-gray-400">National ID (NIN): </span><span className="cht-font-mono cht-font-black cht-text-emerald-700">{SAMPLE_CITIZEN.ninFormatted}</span></div>
                    </div>
                  </div>
                  <div className="cht-text-[10px] cht-text-gray-500 cht-text-center cht-border-t cht-border-gray-100 cht-pt-2">
                    Official Specimen • High Resolution PDF Download Available Upon Verification
                  </div>
                </div>
              )}
            </div>

            <div className="cht-flex cht-items-center cht-justify-between cht-text-xs cht-text-gray-500 cht-pt-2">
              <span>Resolution: {activeMeta.resolution}</span>
              <button
                type="button"
                onClick={() => setIsLightboxOpen(false)}
                className="cht-px-4 cht-py-2 cht-rounded-xl cht-bg-gray-100 hover:cht-bg-gray-200 cht-font-bold cht-text-gray-700"
              >
                Close Specimen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NINSlipSamplePreview;
